export const DATE_FORMAT_YMD = "YYYY-MM-DD";

/**
 * Everything the product is configured with, in one file.
 *
 * It lives in `libs/shared` rather than in an app because the values are not
 * an app's business: the same gateways serve the SPA and any future consumer,
 * the ДПС addresses belong to the domain, and a default period is a product
 * decision. A reader who wants to know "what does this talk to and with what
 * defaults" opens this file and nothing else.
 *
 * The exception, deliberately left where it is: the site identity in
 * `libs/seo/src/site-config` — a canonical host, an OG image and JSON-LD are
 * SEO content baked into HTML at build time, not runtime configuration, and
 * `libs/seo` is its only reader.
 */

/**
 * Neither the ЦСК CMP services nor the fiscal server send CORS headers, so
 * every browser request is forwarded through a gateway (see
 * ai/docs/kep-signing.md).
 *
 * Production runs two of them, so each can be scaled and host-allow-listed on
 * its own: one for the ЦСК (`getCmpGatewayUrl`) and one for the ДПС fiscal
 * server (`getFiscalGatewayUrl`). Development has neither — `apps/web` proxies a
 * single `/gate` path (`apps/web/tools/gateway-proxy.mjs`), and
 * `import.meta.env.DEV` is what selects it. Either URL can still be pinned per
 * deployment through its own env var.
 *
 * One resolver function per case rather than a bare constant, so the dev/prod
 * default is decided in exactly one place.
 */
const LOCAL_GATEWAY_URL = "/gate";

/**
 * ЦСК transport: CMP certificate retrieval, CA auto-detection, the CAs.json
 * registry and TSP time stamps — everything that talks to a ЦСК host rather
 * than to the fiscal server. Overridable via `VITE_CMP_GATEWAY_URL`.
 */
export const getCmpGatewayUrl = (): string =>
  import.meta.env.VITE_CMP_GATEWAY_URL ??
  (import.meta.env.DEV ? LOCAL_GATEWAY_URL : "https://cmp.prostocheck.com.ua/");
/**
 * ДПС transport: the fiscal server at `FISCAL_SERVER_URL` (receipts, shifts,
 * Z-reports, `Objects`). Overridable via `VITE_FISCAL_GATEWAY_URL`.
 */
export const getFiscalGatewayUrl = (): string =>
  import.meta.env.VITE_FISCAL_GATEWAY_URL ??
  (import.meta.env.DEV
    ? LOCAL_GATEWAY_URL
    : "https://gate.prostocheck.com.ua/");

/**
 * Overrides the fiscal server address. Left unset, `FISCAL_SERVER_URL` is
 * used: there is no test server to point at, and this product only ever reads.
 */
export const FISCAL_BASE_URL = import.meta.env.VITE_FISCAL_BASE_URL;

/**
 * Store listing for the jkurwa-web gate browser extension — one constant per
 * browser we publish it to. The install guide (`/extension`) shows the entry
 * that matches the visitor's browser (`useExtensionStore`, libs/data-web) and
 * tells every other browser the extension is not available for it yet. Chrome
 * is the only listing today; a new browser is a new constant here plus a line
 * in that hook's maps. Vite only substitutes a literal `import.meta.env.VITE_*`
 * — never a computed key — so each browser needs its own explicit read. Unset
 * until the listing is live, so the guide's link renders with no `href` rather
 * than a broken one. See ai/docs/kep-signing.md.
 */
export const EXTENSION_CHROME_STORE_URL = import.meta.env
  .VITE_EXTENSION_CHROME_STORE_URL;

/**
 * Where the landing's "sign in" links send a visitor: the `/login` page of
 * the signed-in web app (`apps/web`). Override the app's origin, not this
 * path, via `VITE_APP_URL` — `/login` is a fixed route of that app.
 */
export const APP_LOGIN_URL = `${import.meta.env.VITE_APP_URL ?? "https://app.prostocheck.com.ua"}/login`;

/**
 * The one address the ДПС document gives, and the only one that answers —
 * verified with a `ServerState` probe. Earlier notes in this repo also listed
 * `prro.tax.gov.ua`, `prro2.tax.gov.ua` and `cabinet.tax.gov.ua:9443`; none of
 * them serve this API (404, or 415 for every content type).
 *
 * There is **no separate test server**: a document is a test document when it
 * carries `<TESTING>true</TESTING>`, which only matters to a ПРРО that writes.
 * See ai/docs/prro-api.md.
 */
export const FISCAL_SERVER_URL = "https://fs.tax.gov.ua:8643/fs";

/** Public receipt lookup page of the tax authority's electronic cabinet. */
export const TAX_CABINET_CHECK_URL =
  "https://cabinet.tax.gov.ua/cashregs/check";

/** ПРРО report in hryvnia; there is no second currency to support. */
export const DEFAULT_CURRENCY = "UAH";

/** Formatter fallback when no interface language is given; `libs/i18n` owns
 * the real language list and passes it in. */
export const DEFAULT_LOCALE = "uk-UA";

/** How far back the dashboard looks for a register's last Z-report. */
export const DEFAULT_HISTORY_DAYS = 30;

/** The register page opens on the last couple of days — today and yesterday. */
export const DEFAULT_REGISTER_RANGE_DAYS = 1;

/**
 * Every receipt in range costs its own `CheckExt` call (ai/docs/prro-api.md),
 * so the filter form caps how far apart `dateFrom`/`dateTo` may be — both in
 * the date pickers (via `minDate`/`maxDate`) and in validation, for a typed
 * date the pickers didn't block.
 */
export const MAX_REGISTER_RANGE_DAYS = 7;
